/**
 * Navigation Component
 * 
 * A responsive navigation bar that handles both desktop and mobile views.
 * Features include:
 * - Responsive design with mobile menu
 * - Smooth transitions and animations
 * - Accessibility support
 * - Scroll-aware background
 * - Page transition indicators
 */

'use client';

import Link from 'next/link';
import { motion, AnimatePresence } from 'framer-motion';
import { Container } from '@/components/ui/container';
import { Layers, Menu, User, Wrench, X } from 'lucide-react';
import { usePathname } from 'next/navigation';
import { useNavigationStore } from '@/stores/navigation';
import { ThemeToggle } from '@/components/theme-toggle';
import { BrandLogo } from '@/components/brand-logo';

/**
 * Navigation items configuration
 * @type {Array<{name: string, href: string, icon: React.ComponentType, ariaLabel: string}>}
 */
const navItems = [
	{
		name: 'Fractional CTO Services',
		href: '/services',
		icon: Layers,
		ariaLabel: 'View fractional CTO services',
	},
	{
		name: 'Free Tools',
		href: '/#tools',
		icon: Wrench,
		ariaLabel: 'Try the free ROI calculator and tech assessment',
	},
	{
		name: 'About Me',
		href: '/about',
		icon: User,
		ariaLabel: 'Learn about me',
	},
];

// Initialize scroll event listener for navigation state
if (typeof window !== 'undefined') {
	window.addEventListener('scroll', () => {
		useNavigationStore.getState().setScrolled(window.scrollY > 0);
	});
}

/**
 * Main Navigation Component
 * @returns {JSX.Element | null} The navigation bar component
 */
export function Navigation(): JSX.Element | null {
	const pathname = usePathname();

	// Navigation state management
	const { 
		isOpen, 
		scrolled, 
		isTransitioning, 
		setIsOpen, 
		setIsTransitioning, 
		toggleMenu
	} = useNavigationStore();

	/**
	 * Handles keyboard navigation for the menu
	 * @param {React.KeyboardEvent} e - The keyboard event
	 */
	const handleKeyDown = (e: React.KeyboardEvent): void => {
		if (e.key === 'Escape') {
			setIsOpen(false);
		}
	};

	/**
	 * Handles navigation link clicks
	 * @param {string} href - The destination URL
	 */
	const handleLinkClick = (href: string): void => {
		setIsTransitioning(true);
		setIsOpen(false);
		document.body.classList.add('page-transition');
	};

	return (
		<>
			{/* Page transition indicator */}
			{isTransitioning && <div className="loading-indicator" />}
			
			{/* Main navigation bar */}
			<nav
				className={`fixed w-full z-50 transition-all duration-300 ${
					scrolled
						? 'bg-background/98 dark:bg-background/98 backdrop-blur-xl shadow-lg border-b border-border/40'
						: 'bg-background/5 dark:bg-background/5 backdrop-blur-sm'
				}`}
				role="navigation"
				aria-label="Main navigation"
			>
				<Container>
					{/* Navigation header */}
					<div className="flex items-center justify-between h-16 md:h-20">
						{/* Logo */}
						<Link
							href="/"
							className="flex items-center space-x-2 group"
							aria-label="JovianCloudWorks Home"
							onClick={() => handleLinkClick('/')}
						>
							<BrandLogo
								className="h-12 w-auto sm:h-16 md:h-20 transition-transform group-hover:scale-105"
								priority
							/>
						</Link>

						{/* Desktop Navigation Links */}
						<div className="hidden md:flex items-center space-x-6">
							{navItems.map((item) => (
								<Link
									key={item.name}
									href={item.href}
									onClick={() => handleLinkClick(item.href)}
									className="text-sm font-medium text-foreground/80 hover:text-foreground transition-colors"
									aria-label={item.ariaLabel}
								>
									<span className="flex items-center gap-2">
										<item.icon className="w-4 h-4" />
										{item.name}
									</span>
								</Link>
							))}
						</div>

						<div className="flex items-center gap-1 sm:gap-2">
							<ThemeToggle />

							{/* Mobile Menu Toggle Button */}
							<button
								className="md:hidden p-1.5 sm:p-2 hover:bg-purple-500/10 dark:hover:bg-purple-500/10 rounded-lg transition-colors active:scale-95"
								onClick={toggleMenu}
								onKeyDown={handleKeyDown}
								aria-expanded={isOpen}
								aria-label={isOpen ? 'Close menu' : 'Open menu'}
							>
								<span className="sr-only">{isOpen ? 'Close menu' : 'Open menu'}</span>
								{isOpen ? (
									<X className="w-5 h-5 sm:w-6 sm:h-6 text-foreground dark:text-foreground" />
								) : (
									<Menu className="w-5 h-5 sm:w-6 sm:h-6 text-foreground dark:text-foreground" />
								)}
							</button>
						</div>
					</div>

					{/* Mobile Navigation Menu */}
					<AnimatePresence>
						{isOpen && (
							<motion.div
								initial={{ opacity: 0, y: -20 }}
								animate={{ opacity: 1, y: 0 }}
								exit={{ opacity: 0, y: -20 }}
								transition={{ duration: 0.2 }}
								className="fixed top-0 left-0 right-0 bottom-0 min-h-screen bg-background/90 backdrop-blur-sm mobile-menu-blur z-50 flex flex-col items-center justify-center gap-4 sm:gap-6 md:hidden overflow-y-auto"
								role="dialog"
								aria-modal="true"
								aria-label="Mobile navigation menu"
							>
								{/* Mobile Menu Close Button */}
								<button
									className="absolute top-4 right-4 p-2 hover:bg-purple-500/10 dark:hover:bg-purple-500/10 rounded-lg transition-colors active:scale-95"
									onClick={toggleMenu}
									onKeyDown={handleKeyDown}
									aria-label="Close menu"
								>
									<X className="w-6 h-6 text-foreground dark:text-foreground" />
								</button>

								{/* Mobile Navigation Links */}
								<div className="flex flex-col items-center justify-center gap-4 sm:gap-6 w-full max-w-[280px] sm:max-w-xs px-4">
									{navItems.map((item, index) => (
										<motion.div
											key={item.name}
											initial={{ opacity: 0, y: -10 }}
											animate={{ opacity: 1, y: 0 }}
											transition={{ delay: index * 0.1 }}
											className="w-full"
										>
											<Link
												href={item.href}
												className={`flex items-center space-x-3 px-4 sm:px-6 py-3 sm:py-4 text-base sm:text-lg font-medium rounded-xl transition-colors active:scale-95
													${
														pathname === item.href
															? 'text-purple-500 dark:text-purple-500 bg-purple-500/10'
															: 'text-foreground dark:text-foreground hover:text-purple-500 dark:hover:text-purple-500 hover:bg-purple-500/10'
													}`}
												onClick={() => handleLinkClick(item.href)}
												aria-label={item.ariaLabel}
												aria-current={pathname === item.href ? 'page' : undefined}
											>
												<item.icon className="w-4 h-4 sm:w-5 sm:h-5" />
												<span>{item.name}</span>
											</Link>
										</motion.div>
									))}
									<motion.div
										initial={{ opacity: 0, y: -10 }}
										animate={{ opacity: 1, y: 0 }}
										transition={{ delay: navItems.length * 0.1 }}
										className="flex w-full items-center justify-center gap-3 pt-2"
									>
										<span className="text-sm text-muted-foreground">Theme</span>
										<ThemeToggle />
									</motion.div>
								</div>
							</motion.div>
						)}
					</AnimatePresence>
				</Container>
			</nav>
		</>
	);
}
